import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';
import { dataApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';

const CURRENT_YEAR = new Date().getFullYear();

const formatVehicleLabel = (vehicle) => {
  const make = vehicle.make === 'Other' ? (vehicle.otherMake || 'Other') : vehicle.make;
  const model = vehicle.model === 'Other' ? (vehicle.otherModel || '') : vehicle.model;
  const description = [vehicle.year, make, model].filter(Boolean).join(' ');
  return { description: description || 'Vehicle', registration: vehicle.registration || '' };
};

/**
 * LinkVehicleDialog — attach a vehicle to a quote request.
 * Offers the linked customer's existing vehicles first, or quick-creates one
 * (make/model behaviour mirrors VehicleForm, including the "Other" flow and
 * classification flags copied from the makes/models lookup).
 */
const LinkVehicleDialog = ({ isOpen, onClose, customerId, ownerOrganisationId = null, onLinked }) => {
  const [saving, setSaving] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [makesModels, setMakesModels] = useState({});
  const [form, setForm] = useState({
    registration: '',
    make: '',
    otherMake: '',
    model: '',
    otherModel: '',
    year: ''
  });

  // Registration lookup — reuse an existing vehicle rather than duplicating it
  const [regoSuggestions, setRegoSuggestions] = useState([]);
  const [showRegoSuggestions, setShowRegoSuggestions] = useState(false);
  const regoWrapRef = useRef(null);

  useEffect(() => {
    const rego = form.registration.trim();
    if (rego.length < 2) { setRegoSuggestions([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await dataApi.vehicleSearchByRego(rego);
        if (!cancelled) setRegoSuggestions(results || []);
      } catch {
        if (!cancelled) setRegoSuggestions([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.registration]);

  useEffect(() => {
    const handler = (e) => {
      if (regoWrapRef.current && !regoWrapRef.current.contains(e.target)) setShowRegoSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    dataApi.makesModelsList()
      .then((data) => { if (!cancelled) setMakesModels(data || {}); })
      .catch((err) => {
        console.error('Failed to load makes/models:', err);
        if (!cancelled) setMakesModels({ Other: ['Other'] });
      });

    if (customerId) {
      setVehiclesLoading(true);
      dataApi.customerVehicles(customerId)
        .then((data) => { if (!cancelled) setVehicles(data || []); })
        .catch((err) => {
          console.error('Failed to load customer vehicles:', err);
          if (!cancelled) setVehicles([]);
        })
        .finally(() => { if (!cancelled) setVehiclesLoading(false); });
    } else {
      setVehicles([]);
    }

    return () => { cancelled = true; };
  }, [isOpen, customerId]);

  const getMakeOptions = () => {
    if (!makesModels || typeof makesModels !== 'object') return [];
    const makes = Object.keys(makesModels).filter((key) => key !== '_records').sort();
    const ordered = makes.includes('Other')
      ? ['Other', ...makes.filter((m) => m !== 'Other')]
      : makes;
    return ordered.map((make) => ({ value: make, label: make }));
  };

  const getModelOptions = () => {
    if (!form.make || !makesModels || typeof makesModels !== 'object') return [];
    const models = makesModels[form.make] || [];
    const ordered = models.includes('Other')
      ? ['Other', ...models.filter((m) => m !== 'Other')]
      : models;
    return ordered.map((model) => ({ value: model, label: model }));
  };

  // Mirrors VehicleForm: "Other" make forces model to "Other"
  const handleMakeChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, make: value };
      if (value === 'Other') {
        next.model = 'Other';
      } else {
        next.model = '';
        next.otherMake = '';
        next.otherModel = '';
      }
      return next;
    });
  };

  const handleModelChange = (value) => {
    setForm((prev) => ({
      ...prev,
      model: value,
      otherModel: value === 'Other' ? prev.otherModel : ''
    }));
  };

  const handleCreate = async () => {
    if (!form.registration.trim() && !form.make) {
      toast.error('Please enter a registration or select the make');
      return;
    }
    if (form.make === 'Other' && !form.otherMake.trim()) {
      toast.error('Please specify the vehicle make');
      return;
    }
    if (form.model === 'Other' && form.make !== 'Other' && !form.otherModel.trim()) {
      toast.error('Please specify the vehicle model');
      return;
    }

    setSaving(true);
    try {
      const vehicleData = {
        // Empty when no customer is linked yet — backfilled once a customer is
        // added to the quote request (vehicles can be captured customer-first or vehicle-first)
        customerId: customerId || '',
        registration: form.registration.trim().toUpperCase(),
        make: form.make || '',
        model: form.model || '',
        year: form.year ? parseInt(form.year, 10) : null
      };
      if (form.make === 'Other') vehicleData.otherMake = form.otherMake.trim();
      if (form.model === 'Other') vehicleData.otherModel = form.otherModel.trim();
      // Business quote — the business owns the vehicle
      if (ownerOrganisationId) vehicleData.ownerOrganisation = ownerOrganisationId;

      // Copy classification flags from the makes/models lookup — these drive quote modifiers
      if (form.make && form.make !== 'Other' && form.model && form.model !== 'Other') {
        const match = (makesModels?._records || []).find(
          (record) => record.make === form.make && record.model === form.model
        );
        if (match) {
          vehicleData.bodyType = match.bodyType || null;
          vehicleData.isLuxury = Boolean(match.isLuxury);
          vehicleData.isCommercial = Boolean(match.isCommercial);
          vehicleData.isEuropean = Boolean(match.isEuropean);
          // Only a model that is always electric; the vehicle form's own box
          // covers a model sold both ways.
          vehicleData.electricVehicle = Boolean(match.isElectric);
        }
      }

      const vehicle = await dataApi.vehicleCreate(vehicleData);
      onLinked(vehicle);
    } catch (err) {
      console.error('Failed to create vehicle:', err);
      toast.error(err.message || 'Failed to create vehicle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link Vehicle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {customerId && (vehiclesLoading || vehicles.length > 0) && (
            <div className="space-y-2">
              {vehiclesLoading ? (
                <>
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </>
              ) : (
                vehicles.map((vehicle) => {
                  const { description, registration } = formatVehicleLabel(vehicle);
                  return (
                    <button
                      key={vehicle.$id}
                      type="button"
                      disabled={saving}
                      onClick={() => onLinked(vehicle)}
                      className="w-full flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left hover:bg-accent transition-colors"
                    >
                      <span className="text-sm font-medium">{description}</span>
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        {registration && <span className="font-mono">{registration}</span>}
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {customerId && (vehiclesLoading || vehicles.length > 0) && (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or add new</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 relative" ref={regoWrapRef}>
              <Label htmlFor="link-vehicle-rego">Registration</Label>
              <Input
                id="link-vehicle-rego"
                value={form.registration}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    registration: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
                  }));
                  setShowRegoSuggestions(true);
                }}
                onFocus={() => setShowRegoSuggestions(true)}
                autoComplete="off"
                disabled={saving}
              />
              {showRegoSuggestions && regoSuggestions.length > 0 && (
                <div className="absolute z-[70] left-0 top-full mt-1 w-72 rounded-md border bg-popover shadow-md overflow-hidden">
                  <div className="max-h-56 overflow-y-auto py-1">
                    {regoSuggestions.map((vehicle) => {
                      const { description, registration } = formatVehicleLabel(vehicle);
                      return (
                        <button
                          key={vehicle.$id}
                          type="button"
                          onClick={() => onLinked(vehicle)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-accent transition-colors"
                        >
                          <span className="text-sm font-medium truncate">{description}</span>
                          {registration && <span className="text-xs font-mono text-muted-foreground shrink-0">{registration}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-vehicle-year">Year</Label>
              <Input
                id="link-vehicle-year"
                type="number"
                min="1900"
                max={CURRENT_YEAR + 1}
                value={form.year}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  year: e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                }))}
                disabled={saving}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Make</Label>
              <Select
                value={form.make || undefined}
                onValueChange={handleMakeChange}
                disabled={saving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select make" />
                </SelectTrigger>
                <SelectContent>
                  {getMakeOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Model</Label>
              <Select
                value={form.make === 'Other' ? 'Other' : (form.model || undefined)}
                onValueChange={handleModelChange}
                disabled={saving || !form.make || form.make === 'Other'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {getModelOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.make === 'Other' && (
              <div className="space-y-1.5">
                <Label htmlFor="link-vehicle-other-make">Specify make</Label>
                <Input
                  id="link-vehicle-other-make"
                  placeholder="Enter make name"
                  value={form.otherMake}
                  onChange={(e) => setForm((prev) => ({ ...prev, otherMake: e.target.value }))}
                  disabled={saving}
                />
              </div>
            )}
            {form.model === 'Other' && (
              <div className="space-y-1.5 col-start-2">
                <Label htmlFor="link-vehicle-other-model">Specify model</Label>
                <Input
                  id="link-vehicle-other-model"
                  placeholder="Enter model name"
                  value={form.otherModel}
                  onChange={(e) => setForm((prev) => ({ ...prev, otherModel: e.target.value }))}
                  disabled={saving}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? 'Adding…' : 'Add & link vehicle'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkVehicleDialog;
